/**
 * GlobalLoading.tsx
 * =================
 * 全局加载状态组件 - 提供清晰的视觉反馈
 *
 * 特性：
 * 1. 多种加载模式（Spinner/骨架屏/进度条）
 * 2. 动态提示文案（根据当前阶段自动调整）
 * 3. 预估剩余时间显示
 * 4. 取消操作支持
 */

import React from 'react';

export type LoadingStage = 
  | 'submitting'      // 提交查询中
  | 'querying'        // 查询数据源中
  | 'processing'      // AI处理数据中
  | 'generating'      // 生成报告中
  | 'downloading';    // 下载文件中

interface LoadingMessage {
  title: string;
  subtitle: string;
  icon: string;
}

const STAGE_MESSAGES: Record<LoadingStage, LoadingMessage> = {
  submitting: {
    title: '正在提交查询请求',
    subtitle: '请稍候，系统正在准备...',
    icon: '📤',
  },
  querying: {
    title: '正在查询全球药品数据库',
    subtitle: '正在从 FDA / EMA / CDE / NMPA 等数据源获取最新数据...',
    icon: '🔍',
  },
  processing: {
    title: 'AI 正在智能整合分析数据',
    subtitle: '系统正在使用大语言模型进行深度分析和报告撰写...',
    icon: '🤖',
  },
  generating: {
    title: '正在生成专业报告',
    subtitle: '将所有数据整合为 Word 格式的专业医药情报报告...',
    icon: '📝',
  },
  downloading: {
    title: '正在下载报告文件',
    subtitle: '请保持网络连接稳定，即将完成...',
    icon: '📥',
  },
};

interface GlobalLoadingProps {
  stage: LoadingStage;
  progress?: number;           // 0-100
  elapsedTime?: number;       // 已用时间（秒）
  estimatedRemaining?: number;// 预估剩余时间（秒）
  onCancel?: () => void;       // 取消回调
  dataSourceStatuses?: Array<{
    name: string;
    status: string;
    message?: string;
  }>;
}

const GlobalLoading: React.FC<GlobalLoadingProps> = ({
  stage,
  progress = 0,
  elapsedTime = 0,
  estimatedRemaining,
  onCancel,
  dataSourceStatuses,
}) => {
  const message = STAGE_MESSAGES[stage];

  const formatTime = (seconds: number): string => {
    if (seconds < 60) return `${seconds}秒`;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}分${s}秒`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="max-w-md w-full mx-4 p-8 bg-card rounded-2xl shadow-2xl border border-border animate-fadeIn">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="text-5xl mb-3 animate-bounce">{message.icon}</div>
          <h3 className="text-xl font-bold text-foreground mb-2">{message.title}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">{message.subtitle}</p>
        </div>

        {/* Progress Bar */}
        {progress > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">总体进度</span>
              <span className="text-lg font-bold text-primary">{Math.round(progress)}%</span>
            </div>
            <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary to-blue-500 rounded-full transition-all duration-500 ease-out relative"
                style={{ width: `${progress}%` }}
              >
                <div className="absolute inset-0 bg-white/20 animate-pulse" />
              </div>
            </div>
          </div>
        )}

        {/* Time Info */}
        {(elapsedTime > 0 || estimatedRemaining !== undefined) && (
          <div className="flex items-center justify-center gap-6 mb-6 text-sm">
            {elapsedTime > 0 && (
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>已用时 <strong className="text-foreground">{formatTime(elapsedTime)}</strong></span>
              </div>
            )}
            {estimatedRemaining !== undefined && estimatedRemaining > 0 && (
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span>预计剩余 <strong className="text-primary">{formatTime(estimatedRemaining)}</strong></span>
              </div>
            )}
          </div>
        )}

        {/* Data Source Status (Optional) */}
        {dataSourceStatuses && dataSourceStatuses.length > 0 && (
          <div className="mb-6 max-h-40 overflow-y-auto space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground mb-2">数据源状态</p>
            {dataSourceStatuses.slice(0, 6).map((ds, idx) => (
              <div key={idx} className="flex items-center justify-between text-xs px-2 py-1.5 rounded bg-muted/30">
                <span className="truncate text-foreground">{ds.name}</span>
                <span className={`flex items-center gap-1 ${
                  ds.status === 'success' ? 'text-success' :
                  ds.status === 'error' ? 'text-destructive' :
                  ds.status === 'running' ? 'text-primary' :
                  'text-muted-foreground'
                }`}>
                  {ds.status === 'running' && (
                    <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25"/>
                      <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                    </svg>
                  )}
                  {ds.message || ds.status}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Cancel Button */}
        {onCancel && (
          <button
            onClick={onCancel}
            className="w-full py-2.5 px-4 border border-border rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            取消操作 (Esc)
          </button>
        )}

        {/* Tip */}
        <p className="text-[10px] text-center text-muted-foreground mt-4">
          💡 复杂查询可能需要 3-10 分钟，请耐心等待
        </p>
      </div>
    </div>
  );
};

export default GlobalLoading;
